import { createContext, PropsWithChildren, useEffect, useRef, useState } from "react"
import { useLocation, useNavigate } from 'react-router-dom'
import cookie from 'js-cookie';
import { authRoutes } from "@/utils/constant";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Api, authApiUrl, profileApiUrl } from "@/services";
import { Rider } from "@/types";
import { getErrorResponseMessage, LoginFormValue } from "@/lib";
import { useToast } from "@/hooks/use-toast";

type IContext = {
  logout(): void;
  rider?: Rider;
  loginWithOtp(data: VerifyOTP, isRegister?: boolean): Promise<void>;
  login(data: LoginFormValue): Promise<void>;
  isLoggingWithOtp: boolean;
  isOnline: boolean;
}

type VerifyOTP = {
  phone: string;
  otp: string;
}

export const AuthenticationContext = createContext<IContext>({
  logout: function (): void {
    throw new Error("Function not implemented.");
  },
  loginWithOtp: function (data: VerifyOTP): Promise<void> {
    throw new Error("Function not implemented.");
  },
  isLoggingWithOtp: false,
  login: function (data: LoginFormValue): Promise<void> {
    throw new Error("Function not implemented.");
  },
  isOnline: false,
});

export function AuthenticationProvider({ children }: PropsWithChildren) {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>()
  const [rider, setRider] = useState<Rider>()
  const [isOnline, setIsOnline] = useState<boolean>(false)
  const [newRegister, setNewRegister] = useState<boolean>(false)

  const { pathname } = useLocation()

  const isMounted = useRef(false)

  const { toast } = useToast()

  const navigate = useNavigate()

  const { data } = useQuery<Rider>({
    queryKey: ["profile"],
    queryFn: async () => (await Api.get(profileApiUrl.profile)).data?.data,
    enabled: !!isAuthenticated,
  })

  useEffect(() => {
    if (data && !isMounted.current) {
      setRider(data)
      isMounted.current = true
    }
  }, [data])

  useEffect(() => {
    if (data?.status) setIsOnline(data?.status == 'online')
  }, [data?.status])

  const { mutateAsync: mutateLoginOtp, isPending } = useMutation({
    mutationFn: async (data: VerifyOTP) => (await Api.post(authApiUrl.loginOtp, data)).data
  })

  const { mutateAsync: mutateLogin } = useMutation({
    mutationFn: async (body: Partial<LoginFormValue>) => (await Api.post(authApiUrl.login, body)).data
  })

  const { mutateAsync: mutateRefreshToken } = useMutation({
    mutationFn: async (body: { refreshToken: string }) => (await Api.post(authApiUrl.refresh, body)).data
  })

  const REFRESH_INTERVAL_MINUTES = 10;

  useEffect(() => {
    if (!isAuthenticated) return;

    const refresh = () => {
      const refreshToken = cookie.get("refreshToken") as string;
      if (!refreshToken) return;

      mutateRefreshToken(
        { refreshToken },
        {
          onSuccess(data) {
            cookie.set("token", data?.accessToken)
          },
        }
      );
    };

    refresh();

    const interval = setInterval(refresh, REFRESH_INTERVAL_MINUTES * 60 * 1000);

    return () => clearInterval(interval);
  }, [isAuthenticated, mutateRefreshToken]);

  function logout() {
    setIsAuthenticated(false);
    ['token', 'user'].forEach((item) => cookie.remove(item));
    setRider(undefined);
  }

  async function login(data: LoginFormValue){
    try {
      await mutateLogin(data, {
        onSuccess: (data) => {
          toast({
            title: "Welcome back!",
            description: "You have been successfully logged in.",
          })
          const rider = data?.data?.rider
          cookie.set("token", data?.data?.accessToken)
          cookie.set("refreshToken", data?.data?.refreshToken)
          cookie.set("user", JSON.stringify(rider))
          setAuth(rider)
          navigate('/')
        }
      })
    } catch (error) {
      const message = getErrorResponseMessage(error);

      toast({
        title: "Login Error!",
        description: message,
        variant: "destructive",
      })
    }
  }

  async function loginWithOtp(data: VerifyOTP, isRegister?: boolean){
    await mutateLoginOtp(data, {
      onSuccess: (data) => {
        setNewRegister(true)
        toast({
          title: "Verification successful!",
          description: "You have been logged in successfully.",
        })
        cookie.set("token", data?.data?.accessToken)
        cookie.set("refreshToken", data?.data?.refreshToken)
        const rider = data?.data?.rider
        cookie.set("user", JSON.stringify(rider))
        setAuth(rider)
        if (isRegister) {
          navigate('/auth/onboarding')
        } else {
          navigate('/dashboard')
        }
      },
      onError: (error: any) => {
        const message = getErrorResponseMessage(error);
        toast({
          title: "Verification failed",
          description: message,
          variant: "destructive",
        })
      }
    })
  }

  function setAuth(rider: Rider) {
    setIsAuthenticated(true)
    setRider(rider)
  }

  useEffect(() => {
    if(isAuthenticated !== undefined) {
      if (!isAuthenticated && !authRoutes.includes(pathname)) {
        window.location.href = '/auth/login'
      }

      if (isAuthenticated && authRoutes.includes(pathname) && !newRegister) {
        window.location.href = '/'
      }
    }
  }, [isAuthenticated])

  useEffect(() => {
    const token = cookie.get('token')
    // const user = cookie.get('user')
    if(token) {
      setIsAuthenticated(true)
    } else {
      setIsAuthenticated(false)
    }

    // if (user) {
    //   setRider(JSON.parse(user) as Rider)
    // }
  }, [])

  return (
    <AuthenticationContext.Provider value={{
      logout,
      rider,
      loginWithOtp,
      isLoggingWithOtp: isPending,
      login,
      isOnline
    }}>
      { children }
    </AuthenticationContext.Provider>
  )
}